<template>
  <div :class="{'darkMode': darkMode}">
    <transition name="header-slide">
      <HeaderPage v-show="showHeader" />
    </transition>
    <div id="page-content">
      <slot />
    </div>
    <WhatsAppButton />
    <FooterPage :text="footerText" />
  </div>
</template>

<script>
/**
 * Layout de la web pública — plan 27_09_2026 §4.2 (F1/T5).
 *
 * Extracción LITERAL de lo que hasta ahora vivía en `App.vue`: header con
 * ocultación al hacer scroll, `#page-content` con el offset del header fijo,
 * botón de WhatsApp y footer. No se ha reescrito nada; el único cambio es que
 * el `<router-view />` pasa a ser un `<slot />`, porque ahora es `App.vue`
 * quien decide qué layout envuelve a la vista.
 *
 * Criterio C2 del plan: la web pública debe quedar visualmente idéntica.
 */
import HeaderPage from '@/components/HeaderPage.vue';
import FooterPage from '@/components/FooterPage.vue';
import WhatsAppButton from '@/components/WhatsAppButton.vue';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

export default {
  name: 'PublicLayout',
  components: {
    HeaderPage,
    FooterPage,
    WhatsAppButton,
  },
  setup() {
    const route = useRoute();
    const darkMode = computed(() => route.path === '/onibi');

    const showHeader = ref(true);
    let lastScrollY = 0;

    const SCROLL_THRESHOLD = 120; // no ocultar el header cerca del inicio de la página

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      // Cerca del tope, el header siempre está visible (acceso a navegación y CTA).
      if (currentScroll <= SCROLL_THRESHOLD) {
        showHeader.value = true;
      } else if (currentScroll > lastScrollY + 8) {
        // Solo se oculta tras un desplazamiento descendente claro.
        showHeader.value = false;
      } else if (currentScroll < lastScrollY - 8) {
        showHeader.value = true;
      }
      lastScrollY = currentScroll;
    };

    onMounted(() => {
      window.addEventListener("scroll", handleScroll);
    });

    onUnmounted(() => {
      window.removeEventListener("scroll", handleScroll);
    });

    const footerText = computed(() => {
      return (route.path === '/services' || route.path === '/catalog')
        ? '¿Listo para llevar tu proyecto al siguiente nivel? Hablemos hoy.'
        : '¿Tienes una idea en mente? Cuéntanos y la hacemos realidad.'
    });

    return { footerText, darkMode, showHeader };
  },
};
</script>

<style scoped>
  #page-content {
    /* Compensa la altura del header fijo (token global, sin valores por página). */
    padding-top: var(--header-offset);
    /* Reserva el solape de la tarjeta del footer (que sube con margen negativo)
       más un respiro: así ninguna página termina con su último botón o ficha
       tapado por ella. */
    padding-bottom: calc(var(--footer-card-pull) + var(--space-sm));
    margin: 0;
  }

  .darkMode {
    background-color: rgba(20,20,20,1);
    color: white;
    margin: 0;
    padding: 0;
  }

  .header-slide-enter-active,
  .header-slide-leave-active {
    transition: transform 0.8s ease, opacity 0.8s ease;
  }
  .header-slide-enter-from,
  .header-slide-leave-to {
    transform: translateY(-100%);
    opacity: 0;
  }
  .header-slide-enter-to,
  .header-slide-leave-from {
    transform: translateY(0);
    opacity: 1;
  }
</style>
